import type { StatusRewardBenefit } from '@/app/[locale]/(root)/gamification/all-statuses/_utils/statusRewardData';
import type { StatusRewardBenefitIdType } from '@/components/entities/gamification/GmAwardCard/utils/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { STATUS_REWARD_BENEFITS } from '@/app/[locale]/(root)/gamification/all-statuses/_utils/statusRewardData';
import { playerQueryOptions } from '@/queries/player/player.queries';
import { playerGamificationRewardsOptions } from '@/queries/playerGamificationRewards/playerGamificationRewards.queries';
import { progressStatusesQueryOptions } from '@/queries/progress-statuses/progress-statuses.queries';
import { gamificationMockRewards } from '../constants/gamificationRewardCards';
import { FIRST_PLAYER_LEVEL, getRewardLock } from '../utils/rewardLock';

// Builds the Rewards grid cards from the player's level, the progress statuses and the
// player's grouped rewards: each card gets its availability and the status it requires.

export const useGamificationRewardCards = () => {
  const { data: playerProfile, isPending: isPlayerProfilePending } = useQuery(playerQueryOptions.mePlayerProfile());
  const { data: progressStatuses, isPending: isProgressStatusesPending } = useQuery(progressStatusesQueryOptions.list());

  const { data: groupedRewards, isPending: isGroupedRewardsPending } = useQuery(playerGamificationRewardsOptions.meGroupedRewards());

  const playerLevel = playerProfile?.level ?? FIRST_PLAYER_LEVEL;

  const infoReward = useMemo(() => {
    return new Map<StatusRewardBenefitIdType, StatusRewardBenefit>(
      STATUS_REWARD_BENEFITS.map(r => ([r.id, r])),
    );
  }, [STATUS_REWARD_BENEFITS]);

  const cards = gamificationMockRewards.map(({ action, type, ...reward }) => {
    const { groupedReward, statusName, isDisabled: isRewardDisabled } = getRewardLock({
      type,
      groupedRewards,
      progressStatuses: progressStatuses?.items,
      playerLevel,
    });
    const currentStatusKey = playerProfile?.progressStatus?.presentation?.key;

    const subTitle = (
      currentStatusKey && !isRewardDisabled
        ? infoReward.get(reward.id)?.values[currentStatusKey]
        : undefined
    );

    const availableAmount = (
      isRewardDisabled
        ? 0
        : groupedReward?.availableAmount
    );

    return {
      action,
      disabled: isRewardDisabled,
      reward: {
        ...reward,
        title: reward.id,
        subTitle,
        statusName,
        availableAmount,
      },
    };
  });

  return {
    cards,
    isLoading: isPlayerProfilePending || isProgressStatusesPending || isGroupedRewardsPending,
  };
};
